import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /**
   * Re-places the PLP interstitials whenever the grid's children change. Load
   * more appends product cards, a filter change morphs the whole section back
   * to server order, and a theme-editor block edit patches the card's DOM in
   * place; none of them fires an event (there is no shopify:block:load), so a
   * MutationObserver is the only hook. Our own moves also land here and are
   * absorbed by #placeInterstitials being idempotent.
   */
  #interstitialObserver = new MutationObserver(() => this.#scheduleInterstitials());

  /** @type {number | null} */
  #interstitialFrame = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#placeInterstitials();
    this.#interstitialObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#interstitialObserver.disconnect();
    if (this.#interstitialFrame) cancelAnimationFrame(this.#interstitialFrame);
  }

  #scheduleInterstitials() {
    if (this.#interstitialFrame) cancelAnimationFrame(this.#interstitialFrame);
    this.#interstitialFrame = requestAnimationFrame(() => {
      this.#interstitialFrame = null;
      this.#placeInterstitials();
    });
  }

  /**
   * Content interstitials (VIS-58) are `_media-card` blocks the section renders
   * after the product cards, because Liquid can't read a theme block's
   * position setting from the section. CSS `order` already puts each card in
   * its visual slot; this moves the card's <li> to the same spot in the DOM so
   * keyboard and screen-reader order match what is on screen.
   *
   * A card at position N goes before the product whose data-grid-index is N
   * (or the first one past N, if N is filtered out). One past the products
   * loaded so far stays after the last product, in position order, until a
   * later page brings its slot in. Every move is guarded so a pass over an
   * already-correct grid mutates nothing, which is what stops the observer
   * feeding back into itself.
   */
  #placeInterstitials() {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;

    const interstitials = Array.from(grid.querySelectorAll(':scope > [data-grid-position]'))
      .map((card) => ({
        card,
        position: Number(card instanceof HTMLElement ? card.dataset.gridPosition : NaN),
      }))
      .filter(({ position }) => Number.isFinite(position) && position > 0)
      .sort((a, b) => a.position - b.position);

    if (!interstitials.length) return;

    const products = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const last = products[products.length - 1];
    if (!last) return;

    // A grid that starts at ?page=N has no slot yet for cards placed before
    // its first product: keep them hidden until "load previous" brings it in.
    const firstIndex = Number(products[0]?.dataset.gridIndex);
    for (const { card, position } of interstitials) {
      const hide = position < firstIndex;
      if (card instanceof HTMLElement && card.hidden !== hide) card.hidden = hide;
    }

    // Cards that share a target (same position, or positions the filter left
    // empty) are placed as one contiguous run, so a pass over a correct grid
    // never moves anything.
    /** @type {Map<Element, Element[]>} */
    const before = new Map();
    /** @type {Element[]} */
    const afterLast = [];

    for (const { card, position } of interstitials) {
      const target = products.find((product) => Number(product.dataset.gridIndex) >= position);
      if (target) {
        before.set(target, [...(before.get(target) ?? []), card]);
      } else {
        afterLast.push(card);
      }
    }

    for (const [target, run] of before) {
      let node = target.previousElementSibling;
      const inPlace = run.every((_, i) => {
        const expected = run[run.length - 1 - i];
        const ok = node === expected;
        node = node?.previousElementSibling ?? null;
        return ok;
      });
      if (!inPlace) target.before(...run);
    }

    if (afterLast.length) {
      let node = last.nextElementSibling;
      const inPlace = afterLast.every((card) => {
        const ok = node === card;
        node = node?.nextElementSibling ?? null;
        return ok;
      });
      if (!inPlace) last.after(...afterLast);
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
